/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sipParametersConfig.ts
 🗓️ Created: 2025-01-08
 🧩 Description:
    Configuration for SIP profile parameters including field types
    and possible values. Labels and tooltips are now in i18n JSON files.
────────────────────────────────────────────────────────────────*/

export type FieldType = 'text' | 'number' | 'select' | 'boolean' | 'codec';

export interface SipParameterConfig {
  name: string;
  fieldType: FieldType;
  possibleValues?: string[];
  placeholder?: string;
  hidden?: boolean;
}

// Parameter configurations - labels and tooltips come from i18n/[lang]/sipParameters.json
export const sipParametersConfig: Record<string, SipParameterConfig> = {
  // Debug Settings
  'debug': {
    name: 'debug',
    fieldType: 'select',
    possibleValues: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
  },
  'sip-trace': {
    name: 'sip-trace',
    fieldType: 'select',
    possibleValues: ['yes', 'no']
  },
  'sip-capture': {
    name: 'sip-capture',
    fieldType: 'select',
    possibleValues: ['yes', 'no']
  },
  'watchdog-enabled': {
    name: 'watchdog-enabled',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'watchdog-step-timeout': {
    name: 'watchdog-step-timeout',
    fieldType: 'number',
    placeholder: 'milliseconds'
  },
  'watchdog-event-timeout': {
    name: 'watchdog-event-timeout',
    fieldType: 'number',
    placeholder: 'milliseconds'
  },
  'log-auth-failures': {
    name: 'log-auth-failures',
    fieldType: 'select',
    possibleValues: ['yes', 'no']
  },
  'forward-unsolicited-mwi-notify': {
    name: 'forward-unsolicited-mwi-notify',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },

  // DTMF Settings
  'dtmf-type': {
    name: 'dtmf-type',
    fieldType: 'select',
    possibleValues: ['rfc2833', 'info', 'inband']
  },
  'rfc2833-pt': {
    name: 'rfc2833-pt',
    fieldType: 'number',
    placeholder: '96-127'
  },
  'dtmf-duration': {
    name: 'dtmf-duration',
    fieldType: 'number',
    placeholder: 'milliseconds'
  },

  // Transport Settings
  'sip-port': {
    name: 'sip-port',
    fieldType: 'text',
    placeholder: 'e.g., 5060 or $${external_sip_port}'
  },
  'dialplan': {
    name: 'dialplan',
    fieldType: 'select',
    possibleValues: ['XML', 'Lua', 'JSON']
  },
  'context': {
    name: 'context',
    fieldType: 'text',
    placeholder: 'any dialplan context',
    hidden: true
  },

  // Codec Settings
  'inbound-codec-prefs': {
    name: 'inbound-codec-prefs',
    fieldType: 'codec',
    placeholder: 'e.g., G722,PCMU,PCMA'
  },
  'outbound-codec-prefs': {
    name: 'outbound-codec-prefs',
    fieldType: 'codec',
    placeholder: 'e.g., PCMU,PCMA,G729'
  },
  'hold-music': {
    name: 'hold-music',
    fieldType: 'text',
    placeholder: 'e.g., $${hold_music}'
  },

  // RTP Settings
  'rtp-timer-name': {
    name: 'rtp-timer-name',
    fieldType: 'select',
    possibleValues: ['soft', 'hard']
  },
  'use-rtp-timer': {
    name: 'use-rtp-timer',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'rtp-ip': {
    name: 'rtp-ip',
    fieldType: 'text',
    placeholder: 'e.g., 192.168.1.100 or $${external_rtp_ip}'
  },
  'ext-rtp-ip': {
    name: 'ext-rtp-ip',
    fieldType: 'text',
    placeholder: 'e.g., 203.0.113.1 or $${external_rtp_ip}'
  },
  'media_timeout': {
    name: 'media_timeout',
    fieldType: 'number',
    placeholder: 'seconds'
  },
  'media_hold_timeout': {
    name: 'media_hold_timeout',
    fieldType: 'number',
    placeholder: 'seconds'
  },
  'rtp-timeout-sec': {
    name: 'rtp-timeout-sec',
    fieldType: 'number',
    placeholder: 'seconds'
  },
  'rtp-hold-timeout-sec': {
    name: 'rtp-hold-timeout-sec',
    fieldType: 'number',
    placeholder: 'seconds'
  },

  // NAT/ACL Settings
  'local-network-acl': {
    name: 'local-network-acl',
    fieldType: 'text',
    placeholder: 'ACL name'
  },
  'apply-nat-acl': {
    name: 'apply-nat-acl',
    fieldType: 'text',
    placeholder: 'ACL name'
  },
  'apply-inbound-acl': {
    name: 'apply-inbound-acl',
    fieldType: 'text',
    placeholder: 'ACL name'
  },
  'apply-candidate-acl': {
    name: 'apply-candidate-acl',
    fieldType: 'text',
    placeholder: 'ACL name'
  },
  'apply-register-acl': {
    name: 'apply-register-acl',
    fieldType: 'text',
    placeholder: 'ACL name'
  },
  'NDLB-force-rport': {
    name: 'NDLB-force-rport',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'NDLB-received-in-nat-reg-contact': {
    name: 'NDLB-received-in-nat-reg-contact',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'aggressive-nat-detection': {
    name: 'aggressive-nat-detection',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },


  // Presence Settings
  'manage-presence': {
    name: 'manage-presence',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'presence-hosts': {
    name: 'presence-hosts',
    fieldType: 'text',
    placeholder: 'e.g., example.com or $${domain}'
  },
  'presence-privacy': {
    name: 'presence-privacy',
    fieldType: 'text',
    placeholder: 'e.g., none, private, full or $${presence_privacy}'
  },

  // Codec Negotiation
  'inbound-codec-negotiation': {
    name: 'inbound-codec-negotiation',
    fieldType: 'select',
    possibleValues: ['generous', 'greedy']
  },
  'inbound-late-negotiation': {
    name: 'inbound-late-negotiation',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },

  // Security/Authentication
  'nonce-ttl': {
    name: 'nonce-ttl',
    fieldType: 'number',
    placeholder: 'seconds'
  },
  'auth-calls': {
    name: 'auth-calls',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'auth-subscriptions': {
    name: 'auth-subscriptions',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'inbound-reg-force-matching-username': {
    name: 'inbound-reg-force-matching-username',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'auth-all-packets': {
    name: 'auth-all-packets',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'challenge-realm': {
    name: 'challenge-realm',
    fieldType: 'select',
    possibleValues: ['auto_from', 'auto_to', 'custom']
  },

  // TLS Settings
  'tls': {
    name: 'tls',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'tls-only': {
    name: 'tls-only',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'tls-bind-params': {
    name: 'tls-bind-params',
    fieldType: 'text',
    placeholder: 'e.g., transport=tls'
  },
  'tls-sip-port': {
    name: 'tls-sip-port',
    fieldType: 'text',
    placeholder: 'e.g., 5061 or $${external_tls_port}'
  },
  'tls-passphrase': {
    name: 'tls-passphrase',
    fieldType: 'text',
    placeholder: 'string'
  },
  'tls-verify-date': {
    name: 'tls-verify-date',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'tls-verify-policy': {
    name: 'tls-verify-policy',
    fieldType: 'select',
    possibleValues: ['none', 'insecure', 'all']
  },
  'tls-verify-depth': {
    name: 'tls-verify-depth',
    fieldType: 'number',
    placeholder: '1-10'
  },
  'tls-version': {
    name: 'tls-version',
    fieldType: 'select',
    possibleValues: ['tlsv1', 'tlsv1.1', 'tlsv1.2', 'tlsv1.3']
  },
  'tls-ciphers': {
    name: 'tls-ciphers',
    fieldType: 'text',
    placeholder: 'OpenSSL cipher list'
  },
  'tls-cert-dir': {
    name: 'tls-cert-dir',
    fieldType: 'text',
    placeholder: 'filesystem path'
  },
  'tls-verify-in-subjects': {
    name: 'tls-verify-in-subjects',
    fieldType: 'text',
    placeholder: 'comma-separated list'
  },

  // Registration Settings
  'force-register-domain': {
    name: 'force-register-domain',
    fieldType: 'text',
    placeholder: 'domain name'
  },
  'force-subscription-domain': {
    name: 'force-subscription-domain',
    fieldType: 'text',
    placeholder: 'domain name'
  },
  'force-register-db-domain': {
    name: 'force-register-db-domain',
    fieldType: 'text',
    placeholder: 'domain name'
  },
  'multiple-registrations': {
    name: 'multiple-registrations',
    fieldType: 'select',
    possibleValues: ['contact', 'true', 'false']
  },
  'accept-blind-reg': {
    name: 'accept-blind-reg',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'accept-blind-auth': {
    name: 'accept-blind-auth',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'accept-exact-reg-only': {
    name: 'accept-exact-reg-only',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'disable-register': {
    name: 'disable-register',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },

  // Advanced Settings
  'shutdown-on-fail': {
    name: 'shutdown-on-fail',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'enable-100rel': {
    name: 'enable-100rel',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'enable-3pcc': {
    name: 'enable-3pcc',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'disable-srv503': {
    name: 'disable-srv503',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'disable-transfer': {
    name: 'disable-transfer',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'dbname': {
    name: 'dbname',
    fieldType: 'text',
    placeholder: 'e.g., share_presence'
  },
  'stun-enabled': {
    name: 'stun-enabled',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'stun-auto-disable': {
    name: 'stun-auto-disable',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },
  'enable-rfc-5626': {
    name: 'enable-rfc-5626',
    fieldType: 'select',
    possibleValues: ['true', 'false']
  },

  // Transport & Binding
  'sip-ip': {
    name: 'sip-ip',
    fieldType: 'text',
    placeholder: 'IP or variable'
  },
  'ext-sip-ip': {
    name: 'ext-sip-ip',
    fieldType: 'text',
    placeholder: 'IP or variable'
  },
  'ws-binding': {
    name: 'ws-binding',
    fieldType: 'text',
    placeholder: 'e.g., 127.0.0.1:5066 (localhost only, internal use)'
  },
  'wss-binding': {
    name: 'wss-binding',
    fieldType: 'text',
    placeholder: 'e.g., :7443 (direct FreeSWITCH WSS connection)'
  },

  // Recording
  'record-path': {
    name: 'record-path',
    fieldType: 'text',
    placeholder: 'e.g., /var/recordings'
  },
  'record-template': {
    name: 'record-template',
    fieldType: 'text',
    placeholder: 'e.g., ${uuid}.wav'
  },

  // SRTP Security
  'rtp-secure-media': {
    name: 'rtp-secure-media',
    fieldType: 'select',
    possibleValues: ['false', 'optional', 'true']
  },
  'require-secure-media': {
    name: 'require-secure-media',
    fieldType: 'select',
    possibleValues: ['false', 'true']
  }
};

// Helper function to get parameter configuration
export function getParameterConfig(paramName: string): SipParameterConfig | undefined {
  return sipParametersConfig[paramName];
}

// Helper function to get label for a parameter (uses i18n)
// This function should be called with the t() function from useI18n hook
export function getParameterLabel(paramName: string, t: (key: string, options?: any) => string): string {
  const translationKey = `sipParameters:params.${paramName}.label`;
  const translated = t(translationKey);
  // If translation not found (returns key or path string like params.accept-exact-reg-only.label), fall back cleanly
  if (!translated || translated === translationKey || translated.startsWith('params.') || translated.includes('params.')) {
    const altKey = `sipParameters:${paramName}.label`;
    const altTranslated = t(altKey);
    if (altTranslated && altTranslated !== altKey && !altTranslated.startsWith('params.') && !altTranslated.includes('params.')) {
      return altTranslated;
    }
    return paramName;
  }
  return translated;
}

// Helper function to get tooltip for a parameter (uses i18n)
export function getParameterTooltip(paramName: string, t: (key: string, options?: any) => string): string {
  const translationKey = `sipParameters:params.${paramName}.tooltip`;
  const translated = t(translationKey);
  // If translation not found (returns key or path string), return empty string or fallback
  if (!translated || translated === translationKey || translated.startsWith('params.') || translated.includes('params.')) {
    const altKey = `sipParameters:${paramName}.tooltip`;
    const altTranslated = t(altKey);
    if (altTranslated && altTranslated !== altKey && !altTranslated.startsWith('params.') && !altTranslated.includes('params.')) {
      return altTranslated;
    }
    return '';
  }
  return translated;
}
